import { faLock, faMobile } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  DialogContent,
  DialogTitle,
  FormHelperText,
  FormLabel,
  Grid,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import AuthenticatorContext from 'components/Authenticator/AuthenticatorContext';
import useFeedback from 'components/Feedback/useFeedback';
import { useDisableMfaMutation } from 'lib/graphql/graphql';
import {
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import {
  CondensedUl,
  DialogBlockGrid,
  MfaForm,
  MobileFriendlyMuiOtpInput,
} from '../../styles';

interface DisableTotpFormProps {
  onClose: () => void
}

interface DisableTotpFormData {
  code?: string
  password?: string
}

export default function DisableTotpForm(props: DisableTotpFormProps) {
  const { onClose } = props;
  const feedback = useFeedback();
  const { requestRefresh } = useContext(AuthenticatorContext);
  const [disableMfaMutation, { loading, error, reset }] = useDisableMfaMutation();
  const formControl = useForm<DisableTotpFormData>({ mode: 'onTouched' });
  const [formMode, setFormMode] = useState<'code' | 'password'>('code');
  const {
    register,
    handleSubmit,
    watch,
  } = formControl;

  const watchCode = watch('code');
  const watchPassword = watch('password');

  useEffect(() => {
    reset();
  }, [watchCode, watchPassword, reset]);

  const onSubmit: SubmitHandler<DisableTotpFormData> = useCallback(async (formData) => {
    await disableMfaMutation({
      variables: {
        fields: {
          code: formMode === 'code' ? formData.code : undefined,
          password: formMode === 'password' ? formData.password : undefined,
        },
      },
    });

    feedback({
      anchor: { horizontal: 'right', vertical: 'bottom' },
      content: <FormattedMessage id="mfa_disable_success" />,
      severity: 'success',
    });
    onClose();
    await requestRefresh();
  }, [feedback, onClose, disableMfaMutation, formMode, requestRefresh]);

  return (
    <FormProvider {...formControl}>
      <MfaForm onSubmit={handleSubmit(onSubmit)}>
        <Grid container flexDirection="row" alignItems="center" paddingLeft={2}>
          <Grid item>
            <FontAwesomeIcon icon={faLock} />
          </Grid>
          <Grid item>
            <DialogTitle>
              <FormattedMessage id="confirm_access" />
            </DialogTitle>
          </Grid>
        </Grid>
        <DialogContent>
          {formMode === 'code' && (
            <DialogBlockGrid container>
              <Grid item display="flex" justifyContent="center">
                <FontAwesomeIcon icon={faMobile} size="3x" />
              </Grid>
              <Grid item display="flex" justifyContent="center">
                <Typography variant="h6">
                  <FormattedMessage id="mfa_code" />
                </Typography>
              </Grid>
              <Grid item display="flex" justifyContent="center" paddingTop={2} paddingBottom={2}>
                <Controller
                  name="code"
                  control={formControl.control}
                  rules={{ validate: (value) => value?.length === 6 }}
                  render={({ field, fieldState }) => (
                    <Box>
                      <MobileFriendlyMuiOtpInput
                        {...field}
                        sx={{ gap: 1 }}
                        length={6}
                      />
                      {fieldState.invalid ? (
                        <FormHelperText error>
                          <FormattedMessage id="mfa_code_invalid" />
                        </FormHelperText>
                      ) : null }
                      {error && (
                        <Alert severity="error" sx={{ marginTop: 2 }} onClose={() => reset()}>
                          <FormattedMessage id="mfa_disable_code_error" />
                        </Alert>
                      )}
                    </Box>
                  )}
                />
              </Grid>
              <Grid item display="flex" justifyContent="center" padding={2}>
                <LoadingButton loading={loading} type="submit" variant="contained" color="secondary" size="large" sx={{ width: '100%' }}>
                  <FormattedMessage id="mfa_verify" />
                </LoadingButton>
              </Grid>
              <Grid item display="flex" justifyContent="flex-start" paddingRight={2} paddingLeft={2} paddingBottom={2}>
                <FormattedMessage id="mfa_code_info" />
              </Grid>
            </DialogBlockGrid>
          )}
          {formMode === 'password' && (
            <DialogBlockGrid container>
              <Grid item display="flex" justifyContent="flex-start" flexDirection="column" padding={2}>
                <Box>
                  <FormLabel>
                    <FormattedMessage id="password" />
                  </FormLabel>
                  <TextField
                    {...register('password', { required: true })}
                    type="password"
                    fullWidth
                  />
                  {error && (
                    <Alert severity="error" sx={{ marginTop: 2 }} onClose={() => reset()}>
                      <FormattedMessage id="mfa_disable_password_error" />
                    </Alert>
                  )}
                </Box>
                <Grid item display="flex" justifyContent="center" padding={2}>
                  <LoadingButton loading={loading} type="submit" variant="contained" color="secondary" size="large" sx={{ width: '100%' }}>
                    <FormattedMessage id="mfa_password_confirm" />
                  </LoadingButton>
                </Grid>
              </Grid>
            </DialogBlockGrid>
          )}
        </DialogContent>
        <DialogContent>
          <DialogBlockGrid container>
            <Grid item>
              <FormattedMessage id="question_having_problems" />
            </Grid>
            <Grid item>
              <CondensedUl>
                <li>
                  <Button
                    component={Link}
                    onClick={() => setFormMode(formMode === 'code' ? 'password' : 'code')}
                    sx={{ textTransform: 'none' }}
                  >
                    <FormattedMessage id={`mfa_disable_${formMode === 'code' ? 'password' : 'code'}`} />
                  </Button>
                </li>
              </CondensedUl>
            </Grid>
          </DialogBlockGrid>
        </DialogContent>
      </MfaForm>
    </FormProvider>
  );
}
